import React, { useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiChevronLeft, FiChevronRight, FiAlertCircle } from "react-icons/fi";
import "./OverviewSections.css";

/** Horizontal scroll-snap row with prev/next buttons on desktop. */
export const HorizontalRow = ({ children, label }) => {
  const ref = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    update();
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [update, children]);

  const scrollBy = (direction) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: direction * Math.max(el.clientWidth * 0.8, 240), behavior: "smooth" });
  };

  return (
    <div className="ov-row">
      {canPrev && (
        <button type="button" className="ov-row__arrow ov-row__arrow--prev" aria-label={`Scroll ${label} left`} onClick={() => scrollBy(-1)}>
          <FiChevronLeft size={18} />
        </button>
      )}
      <div className="ov-row__track" ref={ref} role="list" aria-label={label} tabIndex={0}>
        {children}
      </div>
      {canNext && (
        <button type="button" className="ov-row__arrow ov-row__arrow--next" aria-label={`Scroll ${label} right`} onClick={() => scrollBy(1)}>
          <FiChevronRight size={18} />
        </button>
      )}
    </div>
  );
};

const SkeletonCards = ({ count = 4 }) => (
  <div className="ov-row__track" aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="ov-skel-card">
        <div className="ov-skel ov-skel--img" />
        <div className="ov-skel ov-skel--line" style={{ width: "80%" }} />
        <div className="ov-skel ov-skel--line" style={{ width: "55%" }} />
      </div>
    ))}
  </div>
);

/**
 * One Overview section: heading, one-line description, "View all" link, and its own
 * loading skeleton, error-with-retry and empty states around the content.
 */
const OverviewSection = ({
  id,
  title,
  description,
  viewAllTo,
  viewAllLabel = "View all",
  loading,
  error,
  onRetry,
  isEmpty,
  skeletonCount,
  children,
}) => {
  const headingId = `ov-${id}-title`;

  // Loaded successfully with nothing to show: render nothing at all (no heading, link, card or gap).
  // Errors are never hidden - they get a compact row with Retry.
  if (!loading && !error && isEmpty) return null;

  // A div, not <section>: the app has a global `section { padding: 80px 0 }` rule.
  return (
    <div className="ov-section" role="region" aria-labelledby={headingId}>
      <div className="ov-section__head">
        <div>
          <h2 className="ov-section__title" id={headingId}>
            {title}
          </h2>
          {description && <p className="ov-section__desc">{description}</p>}
        </div>
        {viewAllTo && (
          <Link to={viewAllTo} className="ov-section__link">
            <span>{viewAllLabel}</span>
            <FiArrowRight size={14} aria-hidden="true" />
          </Link>
        )}
      </div>

      {loading ? (
        <SkeletonCards count={skeletonCount} />
      ) : error ? (
        <div className="ov-state ov-state--error" role="alert">
          <FiAlertCircle size={22} aria-hidden="true" />
          <div>
            <div className="ov-state__title">Couldn't load {title.toLowerCase()}</div>
            <div className="ov-state__text">{error}</div>
          </div>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={onRetry}>
            Retry
          </button>
        </div>
      ) : (
        children
      )}
    </div>
  );
};

export default OverviewSection;
